import { Component, Input } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Client, ClientCar } from 'src/typings';
import { CLIENT_CAR_DIALOG_CONFIG, CreateCarComponent } from '../../create-car/create-car.component';
import { ClientService } from '../../client.service';
import { firstValueFrom, take, takeUntil } from 'rxjs';

@Component({
  selector: 'app-cars',
  templateUrl: './cars.component.html',
  styleUrls: ['./cars.component.scss']
})
export class CarsComponent {
  constructor(
    private dialog: MatDialog,
    private clientService: ClientService,
    private router: Router
  ) {}

  @Input() set client(value: Client | undefined) {
    const previousClientId = this._client?.id;
    this._client = value;

    if (previousClientId !== value?.id) {
      this.filterControl.setValue('');
    }
  }

  private _client?: Client;

  deletingId: number | null = null;
  error = '';

  filterControl = new FormControl('');

  get filterQuery() {
    return (this.filterControl.value || '').trim().toLowerCase();
  }

  get filteredCars() {
    const cars = this.client?.cars || [];
    const query = this.filterQuery;

    if (!query) {
      return cars;
    }

    return cars.filter((car) => this.matchesFilter(car, query));
  }

  get carsCountLabel() {
    const total = this.client?.cars?.length || 0;
    const filtered = this.filteredCars.length;

    return this.filterQuery ? `${filtered}/${total}` : String(total);
  }

  get client() {
    return this._client;
  }

  edit(car: ClientCar) {
    const dialog = this.dialog.open(CreateCarComponent, CLIENT_CAR_DIALOG_CONFIG);
    dialog.componentInstance.carId = car.id;
    dialog.componentInstance.clientId = this.client?.id;
    dialog.componentInstance.descriptionControl.setValue(car.description);
    dialog.componentInstance.vinControl.setValue(car.vin);
    const subs = dialog.componentInstance.createCarEvent.pipe(take(1), takeUntil(dialog.afterClosed())).subscribe((newCar) => {
      this.updateCarInList(newCar);
      dialog.close();
      subs.unsubscribe();
    });
  }

  async delete(car: ClientCar) {
    if (this.deletingId !== null) return;
    const confirm = window.confirm('Удалить автомобиль ' + (car.vin || car.description || '') + '?');
    if (!confirm) return;
    this.deletingId = car.id; this.error = '';
    try { await firstValueFrom(this.clientService.deleteClientCar(car.id)); this.removeCarFromList(car.id); }
    catch { this.error = 'Не удалось удалить автомобиль. Попробуйте ещё раз.'; }
    finally { this.deletingId = null; }
  }

  updateCarInList(newCar: ClientCar) {
    if (!this.client) return;
    this.client.cars = this.client.cars || [];
    const index = this.client.cars?.findIndex((car) => car.id === newCar.id);
    if (index == -1 || index == undefined) {
      this.client.cars?.push(newCar);
    } else {
      this.client.cars?.splice(index, 1, newCar);
    }
  }

  removeCarFromList(id: number) {
    if (!this.client) return;
    const index = this.client.cars?.findIndex((car) => car.id === id);
    if (index == -1 || index == undefined) return;
    this.client.cars?.splice(index, 1);
  }

  createCar() {
    const dialog = this.dialog.open(CreateCarComponent, CLIENT_CAR_DIALOG_CONFIG);
    dialog.componentInstance.clientId = this.client?.id;
    const subs = dialog.componentInstance.createCarEvent.pipe(take(1), takeUntil(dialog.afterClosed())).subscribe((newCar) => {
      this.updateCarInList(newCar);
      dialog.close();
      subs.unsubscribe();
    });
  }

  createPartsSelection(car: ClientCar) {
    const queryParams = this.getDocumentQueryParams(car);
    if (!queryParams) return;

    this.router.navigate(['/parts-selection', 'new'], { queryParams });
  }

  createOrder(car: ClientCar) {
    const queryParams = this.getDocumentQueryParams(car);
    if (!queryParams) return;

    this.router.navigate(['/orders', 'create'], { queryParams });
  }

  trackByCar(_: number, car: ClientCar) {
    return car.id;
  }

  private matchesFilter(car: ClientCar, query: string) {
    const vin = String(car.vin || '').toLowerCase();
    const description = String(car.description || '').toLowerCase();

    return vin.includes(query) || description.includes(query);
  }

  private getDocumentQueryParams(car: ClientCar) {
    const clientId = Number(this.client?.id || 0);
    const carId = Number(car?.id || 0);
    if (!clientId || !carId) return null;

    return {
      client_id: clientId,
      client_car_id: carId
    };
  }
}
